/**
 * Manages the desktop header sub-menu drawers.
 *
 * Behaviour:
 * - Top-level nav links with .menu-item-has-children open their associated
 *   .sub-menu panel (id = aria-controls value).
 * - Only one panel is visible at a time.
 * - The nav background (.header__nav-bg) animates its height to match the active
 *   panel's sub-menu height (.header__primary > ul > li > .sub-menu > ul).
 * - Collapses on outside click or when clicking any link inside the sub-menu.
 */

export function initializeInteractiveHeader() {
    const header = document.getElementById('header');
    if (!header) return;

    const headerNav = header.querySelector('.header__nav');
    const navBg = header.querySelector('.header__nav-bg') || header.querySelector('.header__menu-bg');
    const closeBtn = header.querySelector('.header__close');
    const primaryTriggers = header.querySelectorAll('.header__primary > ul > li.menu-item-has-children > a');
    const navTriggers = [...primaryTriggers];

    const subMenus = [];
    navTriggers.forEach(trigger => {
        const panelId = trigger.getAttribute('aria-controls');
        let panel = panelId ? document.getElementById(panelId) : null;
        if (!panel) {
            panel = trigger.closest('li')?.querySelector('.sub-menu');
        }
        if (panel && !subMenus.includes(panel)) {
            subMenus.push(panel);
        }
    });

    let activeTrigger = null;
    let activePanel = null;

    /**
     * Measure height of .header__primary > ul > li > .sub-menu > ul
     *
     * @param {HTMLElement} panel
     * @returns {number}
     */
    const getSubMenuUlHeight = (panel) => {
        if (!panel) return 0;
        const ul = panel.querySelector(':scope > ul') || panel.querySelector('ul');
        if (!ul) return 0;

        const isHidden = getComputedStyle(panel).display === 'none';
        let h = 0;
        if (isHidden) {
            const prevVis = panel.style.visibility;
            const prevPos = panel.style.position;
            const prevDisp = panel.style.display;
            panel.style.visibility = 'hidden';
            panel.style.position = 'absolute';
            panel.style.display = 'block';

            h = ul.offsetHeight || ul.scrollHeight;

            panel.style.visibility = prevVis;
            panel.style.position = prevPos;
            panel.style.display = prevDisp;
        } else {
            h = ul.offsetHeight || ul.scrollHeight;
        }

        return h;
    };

    const setBgHeightForPanel = (panel) => {
        if (!navBg || !headerNav) return;
        const ulHeight = getSubMenuUlHeight(panel);
        if (ulHeight > 0) {
            const baseNavHeight = headerNav.offsetHeight;
            const paddingBottom = 16;
            navBg.style.height = (baseNavHeight + ulHeight + paddingBottom) + 'px';
        } else {
            navBg.style.height = '';
        }
    };

    const resetBgHeight = () => {
        if (!navBg) return;
        navBg.style.height = '';
    };

    const closeAllPanels = () => {
        subMenus.forEach(panel => {
            panel.classList.remove('is-visible');
            panel.setAttribute('aria-hidden', 'true');
        });
        navTriggers.forEach(trigger => {
            trigger.classList.remove('is-active');
            trigger.setAttribute('aria-expanded', 'false');
        });
        document.body.classList.remove('menu-open');
        if (closeBtn) closeBtn.hidden = true;
        if (navBg) {
            navBg.classList.remove('is-visible');
            resetBgHeight();
        }
        if (activeTrigger && document.body.contains(activeTrigger)) {
            activeTrigger.focus();
        }
        activeTrigger = null;
        activePanel = null;
    };

    const openSubMenu = (trigger) => {
        const panelId = trigger.getAttribute('aria-controls');
        let panel = panelId ? document.getElementById(panelId) : null;
        if (!panel) {
            panel = trigger.closest('li')?.querySelector('.sub-menu');
        }
        if (!panel) return;

        // Toggle off if same panel is clicked again.
        if (panel === activePanel) {
            closeAllPanels();
            return;
        }

        // Hide whichever was previously active without resetting backdrop immediately.
        subMenus.forEach(p => {
            if (p !== panel) {
                p.classList.remove('is-visible');
                p.setAttribute('aria-hidden', 'true');
            }
        });
        navTriggers.forEach(t => {
            if (t !== trigger) {
                t.classList.remove('is-active');
                t.setAttribute('aria-expanded', 'false');
            }
        });

        // Reveal the new panel and expand backdrop height to match.
        panel.classList.add('is-visible');
        panel.setAttribute('aria-hidden', 'false');
        trigger.classList.add('is-active');
        trigger.setAttribute('aria-expanded', 'true');

        document.body.classList.add('menu-open');
        if (closeBtn) closeBtn.hidden = false;
        if (navBg) {
            navBg.classList.add('is-visible');
            setBgHeightForPanel(panel);
        }

        activeTrigger = trigger;
        activePanel = panel;
    };

    // Desktop only threshold
    const isDesktop = () => window.innerWidth > 640;

    primaryTriggers.forEach(trigger => {
        trigger.addEventListener('click', (e) => {
            if (!isDesktop()) return;
            e.preventDefault();
            openSubMenu(trigger);
        });
    });

    if (closeBtn) {
        closeBtn.addEventListener('click', (e) => {
            e.preventDefault();
            closeAllPanels();
        });
    }

    if (navBg) {
        navBg.addEventListener('click', (e) => {
            if (e.target === navBg) {
                closeAllPanels();
            }
        });
    }

    // Outside click handler
    document.addEventListener('click', (e) => {
        if (!activePanel) return;

        const isTrigger = activeTrigger && activeTrigger.contains(e.target);
        const isPanel = activePanel && activePanel.contains(e.target);
        const isAnyTrigger = navTriggers.some(t => t.contains(e.target));

        if (!isTrigger && !isPanel && !isAnyTrigger) {
            closeAllPanels();
        }
    });

    // Inside link click handler
    header.addEventListener('click', (e) => {
        const subMenuLink = e.target.closest('.sub-menu a');
        if (subMenuLink && activePanel) {
            closeAllPanels();
        }
    });

    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && activePanel) {
            closeAllPanels();
        }
    });

    document.addEventListener('barba-leave-start', closeAllPanels);

    // Re-measure the active panel on resize so the backdrop tracks reflow.
    window.addEventListener('resize', () => {
        if (activePanel && isDesktop()) {
            setBgHeightForPanel(activePanel);
        } else if (!isDesktop() && activePanel) {
            closeAllPanels();
        }
    });
}

